import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Checkbox,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormSection,
  Input,
  PageShell,
  RowActions,
  SectionCard,
  StatusBadge,
  type DataTableColumn,
} from '@/components/ui';
import {
  useSearchPopularCreateMutation,
  useSearchPopularDeleteMutation,
  useSearchPopularMergedQuery,
  useSearchPopularQuery,
  useSearchPopularReorderMutation,
  useSearchPopularUpdateMutation,
} from '../hooks/useSearchPopularQuery';
import type { PopularSearchAdminRow, PopularSearchMergedItem } from '../types/popularSearch';

export const SearchPopularPage = () => {
  const { t } = useTranslation('common');
  const popularQuery = useSearchPopularQuery();
  const mergedQuery = useSearchPopularMergedQuery();
  const createMutation = useSearchPopularCreateMutation();
  const updateMutation = useSearchPopularUpdateMutation();
  const deleteMutation = useSearchPopularDeleteMutation();
  const reorderMutation = useSearchPopularReorderMutation();

  const [newKeyword, setNewKeyword] = useState<string>('');
  const [newSortOrder, setNewSortOrder] = useState<string>('0');
  const [newActive, setNewActive] = useState<boolean>(true);
  const [deleteTarget, setDeleteTarget] = useState<PopularSearchAdminRow | null>(null);
  const [promotingTerm, setPromotingTerm] = useState<string | null>(null);

  const rows = popularQuery.data ?? [];
  const mergedRows = mergedQuery.data ?? [];

  // 이미 운영자 지정으로 등록된 키워드(중복 승격 방지용).
  const manualKeywordSet = useMemo(
    () => new Set(rows.map((r) => r.keyword.trim().toLowerCase())),
    [rows],
  );

  // AUTO 항목을 운영자 지정(MANUAL)으로 승격: 기존 create mutation 재사용.
  // sortOrder 는 현재 수동 목록 최대값 +1 로 말미에 배치.
  const promoteToManual = (term: string): void => {
    const nextSortOrder =
      rows.reduce((max, r) => Math.max(max, r.sortOrder), -1) + 1;
    setPromotingTerm(term);
    createMutation.mutate(
      { keyword: term, sortOrder: nextSortOrder, active: true },
      { onSettled: () => setPromotingTerm(null) },
    );
  };

  const moveAndReorder = (index: number, direction: -1 | 1): void => {
    const next = index + direction;
    if (next < 0 || next >= rows.length) return;
    const swapped = [...rows];
    const target = swapped[next];
    const source = swapped[index];
    if (!target || !source) return;
    swapped[index] = target;
    swapped[next] = source;
    reorderMutation.mutate(swapped.map((r) => r.id));
  };

  const columns = useMemo<ReadonlyArray<DataTableColumn<PopularSearchAdminRow>>>(
    () => [
      {
        key: 'order',
        header: t('search.column.order', '순서'),
        width: 64,
        align: 'right',
        cell: (r) => (
          <span className="tabular-nums text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {r.sortOrder}
          </span>
        ),
      },
      {
        key: 'keyword',
        header: t('search.column.keyword', '검색어'),
        cell: (r) => (
          <span className="font-medium" style={{ color: 'var(--content-strong)' }}>
            {r.keyword}
          </span>
        ),
      },
      {
        key: 'active',
        header: t('search.column.active', '노출'),
        width: 80,
        cell: (r) =>
          r.active ? (
            <StatusBadge label={t('search.flag.active', '사용중')} variant="success" />
          ) : (
            <StatusBadge label={t('search.flag.inactive', '숨김')} variant="neutral" />
          ),
      },
      {
        key: 'updatedAt',
        header: t('search.column.updatedAt', '수정 시각'),
        width: 168,
        cell: (r) => (
          <span className="tabular-nums text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {new Date(r.updatedAt).toLocaleString('ko-KR', {
              year: '2-digit',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ),
      },
      {
        key: 'actions',
        header: '',
        width: 56,
        align: 'right',
        cell: (r) => {
          const index = rows.findIndex((row) => row.id === r.id);
          return (
            <RowActions
              triggerLabel={`${r.keyword} ${t('search.actions.menu', '액션')}`}
              actions={[
                {
                  key: 'up',
                  label: t('search.action.up', '위로'),
                  disabled: reorderMutation.isPending || index <= 0,
                  onClick: () => moveAndReorder(index, -1),
                },
                {
                  key: 'down',
                  label: t('search.action.down', '아래'),
                  disabled: reorderMutation.isPending || index >= rows.length - 1,
                  onClick: () => moveAndReorder(index, 1),
                },
                {
                  key: 'toggle',
                  label: r.active ? t('search.action.hide', '숨기기') : t('search.action.show', '노출'),
                  disabled: updateMutation.isPending,
                  onClick: () =>
                    updateMutation.mutate({ id: r.id, input: { active: !r.active } }),
                },
                {
                  key: 'delete',
                  label: t('search.action.remove', '삭제'),
                  variant: 'danger',
                  separatorBefore: true,
                  disabled: deleteMutation.isPending,
                  onClick: () => setDeleteTarget(r),
                },
              ]}
            />
          );
        },
      },
    ],
    [
      t,
      rows,
      reorderMutation.isPending,
      updateMutation.isPending,
      deleteMutation.isPending,
    ],
  );

  const mergedColumns = useMemo<ReadonlyArray<DataTableColumn<PopularSearchMergedItem>>>(
    () => [
      {
        key: 'rank',
        header: t('search.merged.rank', '순위'),
        width: 56,
        align: 'right',
        cell: (r) => (
          <span className="tabular-nums text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {r.rank}
          </span>
        ),
      },
      {
        key: 'term',
        header: t('search.column.keyword', '검색어'),
        cell: (r) => (
          <span className="font-medium" style={{ color: 'var(--content-strong)' }}>
            {r.term}
          </span>
        ),
      },
      {
        key: 'source',
        header: t('search.merged.source', '출처'),
        width: 120,
        cell: (r) =>
          r.source === 'MANUAL' ? (
            <StatusBadge label={t('search.merged.sourceManual', '운영자 지정')} variant="info" />
          ) : (
            <StatusBadge label={t('search.merged.sourceAuto', '자동 집계')} variant="neutral" />
          ),
      },
      {
        key: 'hitCount',
        header: t('search.merged.hitCount', '검색 횟수'),
        width: 96,
        align: 'right',
        cell: (r) => (
          <span className="tabular-nums text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {r.source === 'AUTO' ? r.hitCount.toLocaleString('ko-KR') : '-'}
          </span>
        ),
      },
      {
        key: 'promote',
        header: '',
        width: 140,
        align: 'right',
        cell: (r) => {
          if (r.source !== 'AUTO') return null;
          const alreadyManual = manualKeywordSet.has(r.term.trim().toLowerCase());
          return (
            <Button
              variant="outline"
              size="sm"
              disabled={
                alreadyManual ||
                createMutation.isPending ||
                promotingTerm === r.term
              }
              onClick={() => promoteToManual(r.term)}
            >
              {alreadyManual
                ? t('search.merged.alreadyManual', '등록됨')
                : promotingTerm === r.term
                  ? t('search.merged.promoting', '등록 중...')
                  : t('search.merged.promote', '운영자 지정으로 등록')}
            </Button>
          );
        },
      },
    ],
    [t, manualKeywordSet, createMutation.isPending, promotingTerm],
  );

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const keyword = newKeyword.trim();
    if (!keyword) return;
    const sortOrder = Number.parseInt(newSortOrder, 10);
    createMutation.mutate(
      {
        keyword,
        sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
        active: newActive,
      },
      {
        onSuccess: () => {
          setNewKeyword('');
          setNewSortOrder('0');
          setNewActive(true);
        },
      },
    );
  };

  return (
    <PageShell
      title={t('search.title', '인기 검색어')}
      description={t(
        'search.subtitle',
        '운영자가 직접 등록하는 인기 검색어 목록입니다. 노출 순서대로 에버라인 쇼핑몰에 표시됩니다.',
      )}
      data-testid="search-popular-page"
    >
      <SectionCard title={t('search.createTitle', '신규 등록')}>
        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <FormSection density="compact" required label={t('search.column.keyword', '검색어')}>
            <Input
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              placeholder={t('search.placeholder.keyword', '검색어')}
              data-testid="search-keyword-input"
              className="h-8 text-[12px]"
            />
          </FormSection>
          <FormSection density="compact" label={t('search.column.order', '순서')}>
            <Input
              type="number"
              min={0}
              value={newSortOrder}
              onChange={(e) => setNewSortOrder(e.target.value)}
              data-testid="search-sort-input"
              className="h-8 max-w-[120px] text-[12px]"
            />
          </FormSection>
          <FormSection density="compact" label={t('search.column.active', '노출')}>
            <div className="flex items-center gap-2">
              <Checkbox
                id="search-new-active"
                checked={newActive}
                onCheckedChange={(v) => setNewActive(v === true)}
              />
              <label
                htmlFor="search-new-active"
                className="text-[12px]"
                style={{ color: 'var(--content-default)' }}
              >
                {t('search.flag.active', '사용중')}
              </label>
            </div>
          </FormSection>
          <div className="ml-[var(--form-label-width)] flex pl-4">
            <Button
              type="submit"
              size="sm"
              disabled={createMutation.isPending || !newKeyword.trim()}
              data-testid="search-create-button"
            >
              {t('search.action.add', '등록')}
            </Button>
          </div>
        </form>
      </SectionCard>

      <SectionCard title={t('search.listTitle', '인기 검색어 목록')}>
        <div className="mb-2 flex justify-end">
          <Button variant="outline" size="sm" onClick={() => popularQuery.refetch()}>
            {t('search.action.refresh', '새로고침')}
          </Button>
        </div>
        <DataTable
          rows={[...rows]}
          rowKey={(r) => r.id}
          columns={[...columns]}
          stickyHeader
          mobileCardCollapse
          emptyState={
            popularQuery.isPending
              ? t('search.empty.loading', '인기 검색어를 불러오는 중...')
              : t('search.empty.list', '등록된 인기 검색어가 없습니다.')
          }
          ariaLabel={t('search.listTitle', '인기 검색어 목록')}
        />
      </SectionCard>

      <SectionCard title={t('search.merged.title', '현재 노출 통합 미리보기')}>
        <p className="mb-3 text-[12px]" style={{ color: 'var(--content-muted)' }}>
          {t(
            'search.merged.subtitle',
            '운영자 지정 키워드(1순위)와 자동 집계 키워드(2순위)가 병합되어 에버라인 쇼핑몰에 노출되는 실제 순서입니다. 자동 집계 항목은 검색 트래픽이 쌓인 뒤 채워집니다.',
          )}
        </p>
        <div className="mb-2 flex justify-end">
          <Button variant="outline" size="sm" onClick={() => mergedQuery.refetch()}>
            {t('search.action.refresh', '새로고침')}
          </Button>
        </div>
        <DataTable
          rows={[...mergedRows]}
          rowKey={(r) => `${r.source}:${r.term}`}
          columns={[...mergedColumns]}
          stickyHeader
          mobileCardCollapse
          emptyState={
            mergedQuery.isPending
              ? t('search.merged.loading', '통합 미리보기를 불러오는 중...')
              : mergedQuery.isError
                ? t('search.merged.loadFailed', '통합 미리보기를 불러오지 못했습니다.')
                : t('search.merged.empty', '노출 중인 인기 검색어가 없습니다.')
          }
          ariaLabel={t('search.merged.title', '현재 노출 통합 미리보기')}
        />
      </SectionCard>

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('search.deleteTitle', '인기 검색어 삭제')}</DialogTitle>
            <DialogDescription>
              {t('search.confirmDelete', '삭제하시겠습니까?')}
            </DialogDescription>
          </DialogHeader>
          {deleteTarget ? (
            <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
              {deleteTarget.keyword}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">
                {t('search.cancel', '취소')}
              </Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (deleteTarget) {
                  deleteMutation.mutate(deleteTarget.id, {
                    onSuccess: () => setDeleteTarget(null),
                  });
                }
              }}
            >
              {t('search.action.remove', '삭제')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
